import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { create } from 'ionicons/icons';
import { Keyboard } from '@capacitor/keyboard';
import { IonButton, IonContent, IonList, isPlatform, IonIcon } from '@ionic/react';
import img from '../../../assets/foodfight_under_review.png';
import cbbTeams from '../../../data/Sports/CBB.json';
import mlbTeams from '../../../data/Sports/MLB.json';
import nbaTeams from '../../../data/Sports/NBA.json';
import cfbTeams from '../../../data/Sports/NCAAF.json';
import nflTeams from '../../../data/Sports/NFL.json';
import nhlTeams from '../../../data/Sports/NHL.json';
import nwslTeams from '../../../data/Sports/NWSL.json';
import soccerTeams from '../../../data/Sports/SOCCER.json';
import wnbaTeams from '../../../data/Sports/WNBA.json';
import { AppContext } from '../../../global/AppContext';
import '../../../styles/fight/summary/FightSummary.scss';
import { getPastAddresses } from '../../../global/request/user';
import useTeamsUIConfig from '../../../global/useTeamsUIConfig';
import {
  calculateGrandTotal,
  DELIVERY_SERVICE_FEE_CENTS,
  DELIVERY_TIP_PERCENT,
  IN_HOUSE_SERVICE_FEE_CENTS,
  parseFormattedAddress,
  SALES_TAX_PERCENT,
} from '../../../global/util';
import AddressFields from '../../addressFields/AddressFields';
import { AuthContext } from '../../authentication/AuthContext';
import MatchmakingFightCard from '../../fightCardV2.1.0/MatchmakingFightCard';
import ModalHeader from '../ModalHeader';
import { StartFightContext, ACTIONS } from '../StartFightContext';
import ReferFriendModal from '../challenger/ReferFriendModalSummary';
import AddressCompactCard from '../takerFightProcess/AddressCompactCard';
import PriceBreakdownCard from '../takerFightProcess/PriceBreakdownCard';
import TakerError from '../takerFightProcess/TakerError';
import TakerItemCard from '../takerFightProcess/TakerItemCard';
import useCanGo from '../useCanGo';
// import specialsTeams from '../../../data/Sports/SPECIALS.json';

function FightSummary({ pageIndex }) {
  const appCtx = useContext(AppContext);
  const authCtx = useContext(AuthContext);
  const { idToken: bearerToken } = authCtx.tokens;
  const startFightCtx = useContext(StartFightContext);
  const { dispatch: startFightCtxDispatch } = startFightCtx;
  const [showImage, setShowImage] = useState(false);
  const [showReferFriendModal, setShowReferFriendModal] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const [reload, specialsTeams, loading, error] = useTeamsUIConfig();
  const contentRef = useRef();
  const [allTeams, setAllTeams] = useState([
    ...nflTeams,
    ...cfbTeams,
    ...nbaTeams,
    ...nhlTeams,
    ...mlbTeams,
    ...soccerTeams,
    ...cbbTeams,
    ...wnbaTeams,
    ...nwslTeams,
  ]);
  const [showAddressFields, setShowAddressFields] = useState(false);
  const [address, setAddress] = useState(appCtx.deliveryAddress);
  const [addressDetails, setAddressDetails] = useState({
    streetAddress: '',
    apt: '',
    city: '',
    state: '',
    zip: '',
  });
  const [pastAddresses, setPastAddresses] = useState([]);
  const [isAddressValid, setIsAddressValid] = useState(false);

  const [isErrorOpen, setIsErrorOpen] = useState(false);

  // These are in cents
  const subTotal = useMemo(
    () => startFightCtx.items.map(item => item.price * item.amount).reduce((sum, cost) => sum + cost),
    [startFightCtx.items],
  );
  const grandTotal = useMemo(() => calculateGrandTotal(subTotal, appCtx.isDelivery), [subTotal, appCtx.isDelivery]);

  useEffect(() => {
    setAllTeams(prevTeams => [...prevTeams, ...specialsTeams]);
  }, [specialsTeams]);

  const changeCtxAddress = () => {
    if (!isAddressValid) {
      console.error('address in invalid format');
      return;
    }

    const trimmedApt = addressDetails.apt?.replace(' ', '');
    const formattedAddress = `${addressDetails.streetAddress}${trimmedApt ? ` ${trimmedApt}` : ''}, ${
      addressDetails.city
    }, ${addressDetails.state} ${addressDetails.zip}`;

    startFightCtx.dispatch({
      action: ACTIONS.SET_ADDRESS,
      payload: formattedAddress,
    });
    setAddress(formattedAddress);
  };

  const dispatchIsAddressAutocompleted = useCallback(
    isAutocompleted => {
      startFightCtxDispatch({
        action: ACTIONS.SET_ADDRESS_AUTOCOMPLETED,
        payload: isAutocompleted,
      });
    },
    [startFightCtxDispatch],
  );

  const handleSetAddress = () => {
    changeCtxAddress();
    setShowAddressFields(false);
  };

  const handleErrorModalClose = () => {
    setIsErrorOpen(false);
    startFightCtx.dispatch({
      action: ACTIONS.SET_ERROR_MESSAGE,
      payload: '',
    });
  };

  const handleInstructionsChange = event => {
    startFightCtx.dispatch({
      action: ACTIONS.SET_DELIVERY_INSTRUCTIONS,
      payload: event.target.value,
    });
  };

  const paymentRequired = useCallback(async () => {
    let totalAmount = 0.0;
    startFightCtx.items.forEach(item => {
      totalAmount += item.price * item.amount;
    });
    if (totalAmount > 0) {
      startFightCtx.dispatch({
        action: ACTIONS.SET_IS_PAYMENT_REQUIRED,
        payload: true,
      });
    }
  }, [startFightCtx]);

  useEffect(() => {
    const fetchPastAddresses = async () => {
      setPastAddresses(await getPastAddresses(authCtx.tokens.idToken));
    };
    paymentRequired();

    fetchPastAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authCtx.tokens.idToken]);

  useEffect(() => {
    setIsErrorOpen(Boolean(startFightCtx.errorMessage));
  }, [startFightCtx.errorMessage]);

  useEffect(() => {
    const addrDetails = parseFormattedAddress(appCtx.deliveryAddress);
    if (!addrDetails.invalid) {
      setAddressDetails(addrDetails);
      setIsAddressValid(true);
      dispatchIsAddressAutocompleted(appCtx.isAddressAutocompleted);
    } else {
      setIsAddressValid(false);
    }
  }, [appCtx.deliveryAddress, appCtx.isAddressAutocompleted, dispatchIsAddressAutocompleted]);

  useEffect(() => {
    setShowImage(startFightCtx.submitted);
  }, [startFightCtx.submitted]);

  useEffect(() => {
    if (!startFightCtx.isPaymentRequired && startFightCtx.isReferClicked && startFightCtx.foodfightId) {
      setShowReferFriendModal(true);
      startFightCtx.dispatch({
        action: ACTIONS.SET_REFERRAL_MODAL_OPEN,
        payload: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startFightCtx.foodfightId, startFightCtx.isReferClicked]);

  useCanGo(
    pageIndex,
    () => {
      const hasItems = startFightCtx.items.length > 0;
      const hasDeliveryAddress = appCtx.isDelivery
        ? startFightCtx.changedDeliveryAddress || appCtx.deliveryAddress
        : true;
      return hasItems && hasDeliveryAddress;
    },
    [startFightCtx.items, startFightCtx.changedDeliveryAddress, appCtx.deliveryAddress],
  );

  const formatName = fullName => {
    const names = fullName?.trim()?.split(' ');
    if (names?.length > 1) {
      return `${names[0]} ${names[names.length - 1][0]}.`;
    }
    return fullName;
  };

  const findTeamAcronym = teamName => {
    let foundTeam = specialsTeams.find(team => team.name === teamName);
    if (!foundTeam) {
      foundTeam = allTeams.find(team => team.name === teamName);
    }
    const acronym = foundTeam ? foundTeam.acronym : teamName;
    /*
    const words = teamName.split(' ');
    const lastCharOfWord = words[words.length - 1].charAt(0).toUpperCase();

    if (lastCharOfWord === acronym.charAt(acronym.length - 1)) {
      return `${acronym.slice(0, -1)} ${words[words.length - 1]}`;
    }
    
    return `${acronym} ${words[words.length - 1]}`; */
    return acronym;
  };

  const findMakerTeam = () => {
    const getOutcomeTeam = startFightCtx.outcome.name;
    if (startFightCtx.game.sport === 'Specials') {
      if (getOutcomeTeam === startFightCtx.game.teams.home.acronym) {
        return startFightCtx.game.teams.home.acronym;
      }
    }
    if (getOutcomeTeam === startFightCtx.game.teams.home.name) {
      return startFightCtx.game.teams.home.acronym;
    }
    return startFightCtx.game.teams.away.acronym;
  };

  const findTakerTeam = () => {
    const getOutcomeTeam = startFightCtx.outcome.name;
    if (startFightCtx.game.sport === 'Specials') {
      if (getOutcomeTeam === startFightCtx.game.teams.home.acronym) {
        return startFightCtx.game.teams.away.acronym;
      }
    }
    if (getOutcomeTeam === startFightCtx.game.teams.home.name) {
      return startFightCtx.game.teams.away.acronym;
    }
    return startFightCtx.game.teams.home.acronym;
  };

  const findTeamColor = teamName => {
    const DEFAULT_COLOR = '#0000ff';
    let foundTeam = allTeams.find(team => team.name === teamName);
    if (foundTeam === undefined) {
      const teamNameAcr = findTeamAcronym(teamName);
      foundTeam = allTeams.find(team => team.acronym === teamNameAcr);
      // for specials
      if (foundTeam === undefined) {
        foundTeam = specialsTeams.find(team => team.acronym === teamNameAcr);
      }
    }
    const color = foundTeam ? foundTeam.color : DEFAULT_COLOR;
    return color;
  };

  const findMakerColor = () => {
    const getOutcomeTeam = startFightCtx.outcome.name;
    if (startFightCtx.game.sport === 'Specials') {
      if (getOutcomeTeam === startFightCtx.game.teams.home.acronym) {
        return findTeamColor(startFightCtx.game.teams.home.acronym);
      }
      return findTeamColor(startFightCtx.game.teams.away.acronym);
    }
    if (getOutcomeTeam === startFightCtx.game.teams.home.name) {
      return startFightCtx.game.teams.home.color;
    }
    return startFightCtx.game.teams.away.color;
  };

  const findTakerColor = () => {
    const getOutcomeTeam = startFightCtx.outcome.name;
    if (startFightCtx.game.sport === 'Specials') {
      if (startFightCtx.game.sport === 'Specials') {
        if (getOutcomeTeam === startFightCtx.game.teams.home.acronym) {
          return findTeamColor(startFightCtx.game.teams.away.acronym);
        }
        return findTeamColor(startFightCtx.game.teams.home.acronym);
      }
    }
    if (getOutcomeTeam === startFightCtx.game.teams.home.name) {
      return startFightCtx.game.teams.away.color;
    }
    return startFightCtx.game.teams.home.color;
  };

  const findMakerOutcome = () => {
    return `${startFightCtx.outcome.type} ${startFightCtx.outcome.scope}`;
  };

  const getGamePanelData = () => {
    // Maker = right side; taker = left side
    const makerTeam = findMakerTeam();
    const takerTeam = findTakerTeam();
    return {
      sportLeague:
        startFightCtx.game.sport === 'Specials' ? startFightCtx.game.league_name : startFightCtx.game.league.name,
      makerName: formatName(startFightCtx?.challenger?.name),
      makerTeam: takerTeam,
      makerTeamAcronym: findTeamAcronym(takerTeam),
      makerTeamColor: findTakerColor(),
      makerOutcomePhrase: findMakerOutcome(),
      takerTeam: makerTeam,
      takerTeamAcronym: findTeamAcronym(makerTeam),
      takerTeamColor: findMakerColor(),
      makerItems: startFightCtx.items,
    };
  };

  const gamePanelData = getGamePanelData();

  useEffect(() => {
    const tryAddKeyboardListener = async () => {
      try {
        await Keyboard.addListener('keyboardDidShow', info => {
          contentRef.current?.scrollByPoint(0, info.keyboardHeight, 0);
        });
      } catch (err) {
        console.error(err);
      }
    };

    if (isPlatform('ios')) {
      tryAddKeyboardListener();
    }

    return () => {
      const tryRemoveKeyboardListeners = async () => {
        try {
          await Keyboard.removeAllListeners();
        } catch (err) {
          console.error(err);
        }
      };

      if (isPlatform('ios')) {
        tryRemoveKeyboardListeners();
      }
    };
  }, []);

  const handleLocationSelect = location => {
    startFightCtx.dispatch({
      action: ACTIONS.SET_DELIVERY_LATITUDE,
      payload: location.lat,
    });
    startFightCtx.dispatch({
      action: ACTIONS.SET_DELIVERY_LONGITUDE,
      payload: location.lng,
    });
  };

  return (
    <>
      <ModalHeader pageName='Fight Summary' />
      <IonContent ref={contentRef} id='fight-summary-modal-page' className='ion-padding'>
        <div id='game-section'>
          <MatchmakingFightCard fight={gamePanelData} />
        </div>
        <div>
          <div className='section-header-wrapper'>
            <h3 className='selection-name outcome-name'>If you win</h3>
          </div>
          <div id='items-section'>
            <IonList>
              {startFightCtx.items.map(item => {
                return <TakerItemCard key={item.id || item.menu_item_id} item={item} />;
              })}
            </IonList>
            <div className='modify-item'>
              <IonIcon icon={create} />
              <button
                type='button'
                onClick={() => {
                  startFightCtx.dispatch({
                    action: ACTIONS.SET_PAGE_INDEX,
                    pageIndex: startFightCtx.pageIndex - 1,
                  });
                  startFightCtx.navRef?.current?.pop();
                }}
                className='change-text'>
                Edit Item
              </button>
            </div>
            <PriceBreakdownCard
              subtotal={subTotal}
              total={grandTotal}
              isDelivery={appCtx.isDelivery}
              overrideTaxPercent={SALES_TAX_PERCENT}
              overrideTipPercent={DELIVERY_TIP_PERCENT}
              customFeesCents={appCtx.isDelivery ? DELIVERY_SERVICE_FEE_CENTS : IN_HOUSE_SERVICE_FEE_CENTS}
              disclaimer={
                appCtx.isDelivery
                  ? "*Sales tax amount is a temporary hold based on the highest sales tax rate in the US. You will only be charged for the sales tax rate in your opponent's city if your opponent wins."
                  : ''
              }
            />
          </div>
          {appCtx.isDelivery && (
            <div className='address-section'>
              {!showAddressFields ? (
                <AddressCompactCard
                  isAddressValid={isAddressValid}
                  addressLine={address}
                  onEdit={() => setShowAddressFields(true)}
                />
              ) : (
                <>
                  <AddressFields
                    addressDetails={addressDetails}
                    setAddressDetails={setAddressDetails}
                    setIsFormValid={setIsAddressValid}
                    showAutocompleteDefault={false}
                    pastAddresses={pastAddresses}
                    onLocationSelect={handleLocationSelect}
                    setIsAddressAutocompleted={dispatchIsAddressAutocompleted}
                  />
                  <IonButton expand='block' onClick={handleSetAddress} className='set-address-button'>
                    Set Address
                  </IonButton>
                </>
              )}
              <textarea
                placeholder="Delivery instructions. i.e. 'Leave at door'"
                onChange={handleInstructionsChange}
                className='delivery-instructions-textbox'
              />
            </div>
          )}
        </div>
      </IonContent>
      <TakerError
        isOpen={isErrorOpen}
        onClose={handleErrorModalClose}
        setIsOpen={setIsErrorOpen}
        msg={startFightCtx.errorMessage || 'Error finalizing details.'}
        isListenerRequired
      />
      {showImage && (
        <div id='fight-under-review'>
          <img src={img} alt='FoodFight under review' />
        </div>
      )}
      {showReferFriendModal && startFightCtx.foodfightId && (
        <ReferFriendModal
          isOpen={showReferFriendModal}
          onClose={() => setShowReferFriendModal(false)}
          bearerToken={bearerToken}
          foodfightId={startFightCtx.foodfightId} // optional: if you've stored it
        />
      )}
    </>
  );
}

export default FightSummary;
